import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Flex } from "../../../../../components/Flex";
import { AtiraImage } from "../../../../../components/Image";
import { Text } from "../../../../../components/Text";
import { MetaMessageTemplate } from "../../../../../model/integration/dto/meta/MetaMessageTemplate";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";
import { AtiraToast } from "../../../../../utils/AtiraToast";
import { coordinatesFrom, fillPlaceholders } from "../utils/templateVariables";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCopy } from "@fortawesome/free-solid-svg-icons/faCopy";
import { faFileLines } from "@fortawesome/free-solid-svg-icons/faFileLines";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons/faLocationDot";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import { faReply } from "@fortawesome/free-solid-svg-icons/faReply";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons/faUpRightFromSquare";

const Frame = styled.div`
  overflow: hidden;
  border-radius: ${Rounded.md};
  line-height: 0;
  width: 100%;

  img,
  video {
    width: 100%;
    height: auto;
    max-height: 24rem;
    object-fit: contain;
  }
`;

const TemplateText = styled(Text)`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.4;
  unicode-bidi: plaintext;
  text-align: start;
`;

const Footer = styled(TemplateText)`
  opacity: 0.75;
`;

const Buttons = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.xs};
`;

/** Outlined rather than filled: the bubble behind it is purple when we sent
 * the template and white when the thread is being read back, and a border in
 * the inherited colour works on both.
 */
const Button = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  color: inherit;
  text-decoration: none;
  padding: ${Spacing.xs};
  border: 1px solid currentColor;
  border-radius: ${Rounded.md};
  opacity: 0.85;

  &[href]:hover,
  &[role="button"]:hover {
    opacity: 1;
  }

  &[role="button"] {
    cursor: pointer;
  }
`;

const Place = styled(Button)`
  justify-content: flex-start;
  gap: ${Spacing.s};
  padding: ${Spacing.s};
  text-align: start;
`;

type ButtonType = NonNullable<
  MetaMessageTemplate["components"][number]["buttons"]
>[number]["type"];

const BUTTON_ICON: Partial<Record<ButtonType, IconDefinition>> = {
  URL: faUpRightFromSquare,
  PHONE_NUMBER: faPhone,
  QUICK_REPLY: faReply,
  COPY_CODE: faCopy,
};

type Props = {
  components?: MetaMessageTemplate["components"];
  /** Placeholder values keyed as `area:token`, from the send modal's inputs.
   * Without them the placeholders are drawn as they are written.
   */
  values?: Record<string, string>;
};

/** A template drawn the way the customer sees it: header media or text, body,
 * footer and buttons. Shared by the sent message and by the send modal's
 * preview, so the two can never drift apart.
 */
export const WATemplateContent: React.FC<Props> = ({ components, values }) => {
  const { t } = useTranslation();

  const parts = components || [];

  const header = parts.find((component) => component.type === "HEADER");
  const body = parts.find((component) => component.type === "BODY");
  const footer = parts.find((component) => component.type === "FOOTER");
  const buttons =
    parts.find((component) => component.type === "BUTTONS")?.buttons || [];

  const media = header?.example?.header_handle?.[0];

  const onCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    AtiraToast.success(t("common.copy.success"));
  };

  const renderHeader = () => {
    if (!header) {
      return null;
    }

    if (header.format === "TEXT") {
      return (
        <TemplateText fontWeight="bold">
          {fillPlaceholders(header.text, "header", values)}
        </TemplateText>
      );
    }

    if (header.format === "LOCATION") {
      const coordinates = coordinatesFrom(values?.["location:coordinates"]);
      const address = values?.["location:address"];

      return (
        <Place
          href={
            coordinates
              ? `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`
              : undefined
          }
          target="_blank"
          rel="noreferrer"
        >
          <AtiraIcon icon={faLocationDot} />
          <Flex flexDirection="column">
            <Text fontSize="s" fontWeight="bold">
              {values?.["location:name"] || t("common.whatsapp.location")}
            </Text>
            {address ? <Text fontSize="xs">{address}</Text> : null}
          </Flex>
        </Place>
      );
    }

    if (!media) {
      return null;
    }

    if (header.format === "VIDEO") {
      return (
        <Frame>
          <video src={media} controls preload="metadata" />
        </Frame>
      );
    }

    if (header.format === "DOCUMENT") {
      return (
        <Button href={media} target="_blank" rel="noreferrer">
          <AtiraIcon icon={faFileLines} />
          <Text fontSize="s">{t("common.whatsapp.document")}</Text>
        </Button>
      );
    }

    return (
      <Frame>
        <AtiraImage preview src={media} alt={body?.text || "WhatsApp image"} />
      </Frame>
    );
  };

  return (
    <Flex flexDirection="column" gap="s">
      {renderHeader()}

      {body?.text ? (
        <TemplateText fontSize="m">
          {fillPlaceholders(body.text, "body", values)}
        </TemplateText>
      ) : null}

      {footer?.text ? <Footer fontSize="s">{footer.text}</Footer> : null}

      {buttons.length ? (
        <Buttons>
          {buttons.map((button, index) => {
            const icon = BUTTON_ICON[button.type];
            const code =
              button.type === "COPY_CODE"
                ? values?.[`coupon:${index}`]
                : undefined;

            return (
              <Button
                key={`${button.type}-${button.text}`}
                href={
                  button.type === "URL"
                    ? fillPlaceholders(button.url, `button:${index}`, values)
                    : button.type === "PHONE_NUMBER" && button.phone_number
                      ? `tel:${button.phone_number}`
                      : undefined
                }
                target="_blank"
                rel="noreferrer"
                role={code ? "button" : undefined}
                onClick={code ? () => onCopy(code) : undefined}
              >
                {icon ? <AtiraIcon icon={icon} size="sm" /> : null}
                <Text fontSize="s">{button.text}</Text>
              </Button>
            );
          })}
        </Buttons>
      ) : null}
    </Flex>
  );
};
